/** Durable counting data only; authentication credentials never enter this store. */
export type OfflineIdentity = {instance_id: string; user_id: number};
export type OfflineBundle = OfflineIdentity & {
  protocol_version: number;
  bundle_id: string;
  prepared_at: string;
  expires_at: string;
  session: any;
  lines: any[];
  parts: any[];
  locations: any[];
  can_create_stock: boolean;
  persistent_storage?: boolean;
};
export type CountCommand = {
  operation_id: string;
  kind?: 'count' | 'resolution';
  related_operation_id?: string;
  submission_id?: number;
  created_at?: number;
  namespace: string;
  session_id: number;
  payload: Record<string, any>;
  state: 'pending' | 'accepted' | 'needs_review';
  result?: any;
  attempts: number;
  next_attempt_at: number;
};

const DB_NAME = 'techni-serre-offline-counts';
const DB_VERSION = 1;
const notify = () => { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('offline-count-change')); };
// One channel per JS module; received changes notify this tab without rebroadcast.
const changeChannel = typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('techni-serre-offline-count-changes') : null;
if (changeChannel) changeChannel.onmessage = notify;
const changed = () => { notify(); changeChannel?.postMessage('changed'); };
const validateEntryLengths = (payload:Record<string,any>) => {
  for(const [field,max] of [['notes',2000],['batch',100],['identification',1000]] as const){
    if(payload[field]!=null&&String(payload[field]).length>max)throw new Error('The entry is too long. Shorten it before saving.');
  }
};
const identityKey = (identity: OfflineIdentity) => `${identity.instance_id}:${identity.user_id}`;
/** Observation receipts omit preparation-only labels; never carry them to another lot. */
function withPreparedStockBarcode(prepared: any, current: any) {
  return prepared.stock_native_id && prepared.stock_native_id === current.stock_native_id
    && prepared.part_native_id === current.part_native_id && prepared.stock_barcode
    && current.stock_barcode === undefined ? {...current, stock_barcode: prepared.stock_barcode} : current;
}
const requestValue = <T>(request: IDBRequest<T>) => new Promise<T>((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error ?? new Error('Storage request failed.'));
});
const transactionDone = (transaction: IDBTransaction) => new Promise<void>((resolve, reject) => {
  transaction.oncomplete = () => resolve();
  transaction.onabort = () => reject(transaction.error ?? new Error('Storage write was interrupted.'));
  transaction.onerror = () => reject(transaction.error ?? new Error('Storage write failed.'));
});

export class CountStorage {
  private connection?: Promise<IDBDatabase>;

  private open() {
    if (!this.connection) this.connection = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
        if (!db.objectStoreNames.contains('bundles')) db.createObjectStore('bundles');
        if (!db.objectStoreNames.contains('commands')) db.createObjectStore('commands', {keyPath:'operation_id'});
      };
      request.onerror = () => { this.connection = undefined; reject(request.error); };
      request.onblocked = () => { this.connection = undefined; reject(new Error('Close other application tabs to update local storage.')); };
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); this.connection = undefined; };
        resolve(db);
      };
    });
    return this.connection;
  }

  private async read<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
    const db = await this.open();
    return requestValue(db.transaction(store).objectStore(store).get(key));
  }

  async getIdentity(): Promise<OfflineIdentity | null> {
    return (await this.read<OfflineIdentity>('meta', 'active-identity')) ?? null;
  }

  async activateIdentity(identity: OfflineIdentity) {
    if (!identity?.instance_id || !Number.isInteger(identity.user_id) || identity.user_id <= 0) throw new Error('Invalid offline identity.');
    const db = await this.open();
    const transaction = db.transaction('meta', 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore('meta').put({...identity}, 'active-identity');
    await done;
    changed();
  }

  async lockIdentity() {
    const db = await this.open();
    const transaction = db.transaction('meta', 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore('meta').delete('active-identity');
    await done;
    changed();
  }

  private async identity() {
    const identity = await this.getIdentity();
    if (!identity) throw new Error('Reconnect with the same account to open this count.');
    return identity;
  }

  async saveBundle(bundle: OfflineBundle) {
    const identity = await this.identity();
    if (identityKey(identity) !== identityKey(bundle) || bundle.protocol_version !== 1 || !bundle.bundle_id || !bundle.session?.pk || !Array.isArray(bundle.lines) || !Array.isArray(bundle.parts) || !Array.isArray(bundle.locations)) throw new Error('Incomplete offline preparation.');
    const db = await this.open();
    const transaction = db.transaction(['meta','bundles','commands'], 'readwrite');
    const done = transactionDone(transaction);
    // Check again inside the write transaction to prevent a cross-tab account switch.
    const current = await requestValue(transaction.objectStore('meta').get('active-identity'));
    if (!current || identityKey(current) !== identityKey(identity)) { transaction.abort(); await done; return; }
    const commands=await requestValue<CountCommand[]>(transaction.objectStore('commands').getAll());
    const own=commands.filter(command=>command.namespace===identityKey(identity)&&command.session_id===bundle.session.pk);
    if(own.some(command=>command.state==='pending')){transaction.abort();try{await done;}catch{}throw new Error('Synchronize saved entries before refreshing this count.');}
    const updated=structuredClone(bundle);
    for(const command of own){
      const line=command.result?.line;
      if(command.state!=='accepted'||!line||Number(line.session??command.session_id)!==bundle.session.pk)continue;
      const index=updated.lines.findIndex(item=>item.pk===line.pk);
      if(index<0){transaction.abort();try{await done;}catch{}throw new Error('The count changed during preparation. Refresh it online.');}
      if(Number(line.revision)>Number(updated.lines[index].revision))updated.lines[index]=withPreparedStockBarcode(updated.lines[index],line);
    }
    updated.session.counted_line_count=updated.lines.filter(line=>line.counted_quantity!=null).length;
    transaction.objectStore('bundles').put(updated, `${identityKey(identity)}:${bundle.session.pk}`);
    await done;
    changed();
  }

  async getBundle(sessionId: number): Promise<OfflineBundle | undefined> {
    const identity = await this.getIdentity();
    if (!identity) return undefined;
    return this.read('bundles', `${identityKey(identity)}:${sessionId}`);
  }

  async listBundles(): Promise<OfflineBundle[]> {
    const identity = await this.getIdentity();
    if (!identity) return [];
    const db = await this.open();
    const bundles = await requestValue<OfflineBundle[]>(db.transaction('bundles').objectStore('bundles').getAll());
    return bundles.filter(bundle => identityKey(bundle) === identityKey(identity));
  }

  async listCommands(sessionId?: number): Promise<CountCommand[]> {
    const identity = await this.getIdentity();
    if (!identity) return [];
    const db = await this.open();
    const commands = await requestValue<CountCommand[]>(db.transaction('commands').objectStore('commands').getAll());
    return commands.filter(command => command.namespace === identityKey(identity) && (sessionId == null || command.session_id === sessionId)).sort((a,b) => (a.created_at ?? 0) - (b.created_at ?? 0));
  }

  async enqueue(sessionId: number, payload: Record<string, any>): Promise<CountCommand> {
    // Clone before any async boundary so caller edits cannot change the queued intent.
    const data = structuredClone(payload);
    validateEntryLengths(data);
    const identity = await this.identity();
    if (!/^\d+(?:\.\d+)?$/.test(String(data.counted_quantity)) || !Number.isFinite(Number(data.counted_quantity))) throw new Error('Enter a nonnegative counted quantity.');
    const db = await this.open();
    const transaction = db.transaction(['meta','bundles','commands'], 'readwrite');
    const done = transactionDone(transaction);
    const current = await requestValue(transaction.objectStore('meta').get('active-identity'));
    const bundle = await requestValue<OfflineBundle>(transaction.objectStore('bundles').get(`${identityKey(identity)}:${sessionId}`));
    const commands = await requestValue<CountCommand[]>(transaction.objectStore('commands').getAll());
    const fail = async (message: string): Promise<never> => { transaction.abort(); try { await done; } catch {} throw new Error(message); };
    if (!current || identityKey(current) !== identityKey(identity)) return fail('The account changed.');
    if (!bundle || bundle.bundle_id !== data.bundle_id || Date.parse(bundle.expires_at) <= Date.now()) return fail('Refresh this count online before recording new quantities.');
    const sameLine = (previous: Record<string, any>) => data.line_id != null ? previous.line_id === data.line_id : previous.line_id == null && ['part_native_id','location_native_id','batch','identification'].every(key => String(previous[key] ?? '') === String(data[key] ?? ''));
    if (commands.some(command => command.namespace === identityKey(identity) && command.session_id === sessionId && command.kind !== 'resolution' && sameLine(command.payload) && command.state !== 'accepted')) return fail('This line is awaiting synchronization or review.');
    const operationId = crypto.randomUUID();
    const command: CountCommand = {operation_id:operationId,kind:'count',created_at:Date.now(),namespace:identityKey(identity),session_id:sessionId,payload:{...data,operation_id:operationId},state:'pending',attempts:0,next_attempt_at:0};
    transaction.objectStore('commands').add(command);
    await done;
    changed();
    return command;
  }

  async enqueueResolution(sessionId: number, originalOperationId: string, submissionId: number, payload: Record<string, any>): Promise<CountCommand> {
    const data = structuredClone(payload);
    validateEntryLengths(data);
    const identity = await this.identity();
    if (!Number.isSafeInteger(submissionId) || submissionId <= 0) throw new Error('Invalid review submission.');
    const db = await this.open();
    const transaction = db.transaction(['meta','commands'], 'readwrite');
    const done = transactionDone(transaction);
    const current = await requestValue(transaction.objectStore('meta').get('active-identity'));
    const original = await requestValue<CountCommand>(transaction.objectStore('commands').get(originalOperationId));
    const commands = await requestValue<CountCommand[]>(transaction.objectStore('commands').getAll());
    const fail = async (message: string): Promise<never> => { transaction.abort(); try { await done; } catch {} throw new Error(message); };
    if (!current || identityKey(current) !== identityKey(identity)) return fail('The account changed.');
    if (!original || original.namespace !== identityKey(identity) || original.session_id !== sessionId || original.state !== 'needs_review' || original.kind === 'resolution') return fail('No review entry found.');
    if (Number(original.result?.submission_id) !== submissionId) return fail('The review identity changed.');
    const pending = commands.find(command => command.namespace === identityKey(identity) && command.kind === 'resolution' && command.related_operation_id === originalOperationId && command.state === 'pending');
    if (pending) return fail('This review is awaiting synchronization.');
    const operationId = crypto.randomUUID();
    const command: CountCommand = {operation_id:operationId,kind:'resolution',related_operation_id:originalOperationId,submission_id:submissionId,created_at:Date.now(),namespace:identityKey(identity),session_id:sessionId,payload:{...data,operation_id:operationId},state:'pending',attempts:0,next_attempt_at:0};
    transaction.objectStore('commands').add(command);
    await done;
    changed();
    return command;
  }

  async acknowledge(operationId: string, result: any) {
    if (result?.state !== 'accepted' && result?.state !== 'needs_review') throw new Error('The server did not confirm this entry.');
    const identity = await this.identity();
    const db = await this.open();
    const transaction = db.transaction(['meta','commands','bundles'], 'readwrite');
    const done = transactionDone(transaction);
    const current = await requestValue(transaction.objectStore('meta').get('active-identity'));
    const command = await requestValue<CountCommand>(transaction.objectStore('commands').get(operationId));
    if (!current || identityKey(current) !== identityKey(identity) || !command || command.namespace !== identityKey(identity)) { transaction.abort(); try {await done;} catch {} throw new Error('The account changed.'); }
    command.state = result.state;
    command.result = result;
    transaction.objectStore('commands').put(command);
    if (command.kind === 'resolution' && command.related_operation_id && result.state === 'accepted') {
      const original = await requestValue<CountCommand>(transaction.objectStore('commands').get(command.related_operation_id));
      if (original?.namespace === command.namespace && original.state === 'needs_review') {
        original.state = 'accepted';
        original.result = {...original.result,resolved:true,resolved_by:operationId,line:result.line};
        transaction.objectStore('commands').put(original);
      }
      const attempts=await requestValue<CountCommand[]>(transaction.objectStore('commands').getAll());
      for(const previous of attempts){
        if(previous.namespace===command.namespace&&previous.kind==='resolution'&&previous.related_operation_id===command.related_operation_id&&previous.state==='needs_review'){
          previous.state='accepted';previous.result={...previous.result,superseded_by:operationId};transaction.objectStore('commands').put(previous);
        }
      }
    }
    if (result.state === 'accepted' && result.line) {
      const key = `${identityKey(identity)}:${result.line.session ?? command.session_id}`;
      const bundle = await requestValue<OfflineBundle>(transaction.objectStore('bundles').get(key));
      if (bundle) {
        const index = bundle.lines.findIndex(line => line.pk === result.line.pk);
        if (index < 0) bundle.lines.push(result.line);
        else if (Number(bundle.lines[index].revision) <= Number(result.line.revision)) bundle.lines[index] = withPreparedStockBarcode(bundle.lines[index],result.line);
        if (result.session_revision != null) bundle.session.revision = result.session_revision;
        bundle.session.counted_line_count = bundle.lines.filter(line => line.counted_quantity != null).length;
        bundle.session.line_count = bundle.lines.length;
        transaction.objectStore('bundles').put(bundle,key);
      }
    }
    await done;
    changed();
  }

  async defer(operationId: string, delayMs: number) {
    const identity = await this.identity();
    const db = await this.open();
    const transaction = db.transaction('commands','readwrite');
    const done = transactionDone(transaction);
    const command = await requestValue<CountCommand>(transaction.objectStore('commands').get(operationId));
    if (command?.namespace === identityKey(identity) && command.state === 'pending') {
      command.attempts += 1;
      command.next_attempt_at = Date.now() + delayMs;
      transaction.objectStore('commands').put(command);
    }
    await done;
    changed();
  }

  async acquireLease(owner: string, durationMs = 30000): Promise<boolean> {
    const identity = await this.identity();
    const db = await this.open();
    const transaction = db.transaction('meta','readwrite');
    const done = transactionDone(transaction);
    const store = transaction.objectStore('meta');
    const key = `lease:${identityKey(identity)}`;
    const current = await requestValue<{owner:string;expires:number}>(store.get(key));
    const available = !current || current.expires <= Date.now() || current.owner === owner;
    if (available) store.put({owner,expires:Date.now()+durationMs},key);
    await done;
    return available;
  }

  async releaseLease(owner: string) {
    const identity = await this.getIdentity();
    if (!identity) return;
    const db = await this.open();
    const transaction = db.transaction('meta','readwrite');
    const done = transactionDone(transaction);
    const store = transaction.objectStore('meta');
    const key = `lease:${identityKey(identity)}`;
    const current = await requestValue<{owner:string}>(store.get(key));
    if (current?.owner === owner) store.delete(key);
    await done;
  }
}

export const countStorage = new CountStorage();
